import { TimsheetAPIResponse } from './../../../types/timesheetsAPI'
// Libraries
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { timesheetDetailApi } from './timesheetDetailApi'
// Types and interfaces
import { Approval } from '../../slices/timesheet/timesheetPageSlice'
import { Filters, TimesheetsTotals } from '../../slices/timesheet/timesheetDetailSlice'
import { ApprovalRequest, Reject } from '../../slices/timesheet/timesheetApprovalDetails/timesheetApprovalDetailsSlice'
// Utils
import getBaseQueryWithReauth from '../../../utils/api/query-auth'
import { handleError } from '../../../utils/errorHandler'
import getAuth from '../../../utils/api/getToken'
import { rtkQueryCache } from '../services/api-cache'

const REFRESH_TIMEOUT = 15 // 15 seconds

type TimesheetsTotalsParams = Pick<Filters, 'startDate' | 'endDate' | 'driverId' | 'branchId' | 'status'>

const baseQuery = fetchBaseQuery({
	baseUrl: `${process.env.REACT_APP_API_BASE_URL}/Timesheet`,
	prepareHeaders: headers => {
		const token = getAuth()
		if (token) {
			headers.set('authorization', `Bearer ${token.auth}`)
		}
		return headers
	},
})

export const timesheetApi = createApi({
	reducerPath: 'timesheets',
	baseQuery: getBaseQueryWithReauth(baseQuery),
	tagTypes: ['TimesheetTotals', 'TimesheetsFiltered'],
	keepUnusedDataFor: 0,
	endpoints: builder => ({
		getTimesheets: builder.query({
			query: () => `/getTimesheets`,
			keepUnusedDataFor: REFRESH_TIMEOUT,
		}),
		getAllTimesheets: builder.query({
			query: () => `/getTimesheets?PageSize=0`,
			keepUnusedDataFor: REFRESH_TIMEOUT,
		}),
		getTimesheetsForApproval: builder.query({
			query: () => `/getTimesheets?NotSubmitted=true`,
			keepUnusedDataFor: REFRESH_TIMEOUT,
		}),
		getTimesheetsFiltered: builder.query<TimsheetAPIResponse, Filters>({
			query: (filters: Filters) => {
				// Map Filters fields to API query param names
				const { currentPage, pageSize, orderByColumn, ascending, startDate, endDate, driverId, status, branchId } =
					filters

				// Build URL manually to ensure multiple Status parameters are handled correctly
				const params = new URLSearchParams()

				params.set('CurrentPage', String(currentPage))
				params.set('PageSize', String(pageSize))
				params.set('OrderByColumn', orderByColumn)
				params.set('Ascending', String(ascending))
				if (startDate) params.set('StartDate', startDate)
				if (endDate) params.set('EndDate', endDate)

				// Only add DriverId if it's valid (not undefined, not null, and not 0)
				if (driverId !== undefined && driverId !== null && Number(driverId) !== 0) {
					params.set('DriverId', String(driverId))
				}
				// Only add BranchId if it's valid (not undefined, not null, and not 0)
				if (branchId !== undefined && branchId !== null && Number(branchId) !== 0) {
					params.set('BranchId', String(branchId))
				}
				// Add multiple Status parameters
				if (status && Array.isArray(status) && status.length > 0) {
					status.forEach(s => params.append('Status', s))
				} else if (status) {
					params.append('Status', String(status))
				}

				const urlWithParams = `/getTimesheets?${params.toString()}`
				return urlWithParams
			},
			transformResponse: (response: TimsheetAPIResponse, meta, arg: Filters) => {
				// Cache the response for 2 hours
				rtkQueryCache.set('/getTimesheets', arg, response)
				return response
			},
			providesTags: (res, err, filters) => [{ type: 'TimesheetsFiltered', id: JSON.stringify(filters) }],
			// 2 hours in seconds for RTK Query cache
		}),
		getTimesheetsTotals: builder.query<TimesheetsTotals, TimesheetsTotalsParams>({
			query: (filters: TimesheetsTotalsParams) => {
				const { startDate, endDate, driverId, branchId, status } = filters

				// Build URL manually to ensure multiple Status parameters are handled correctly
				const params = new URLSearchParams()

				if (startDate) params.set('StartDate', startDate)
				if (endDate) params.set('EndDate', endDate)

				// Only add DriverId if it's valid (not undefined, not null, and not 0)
				if (driverId !== undefined && driverId !== null && Number(driverId) !== 0) {
					params.set('DriverId', String(driverId))
				}
				// Only add BranchId if it's valid (not undefined, not null, and not 0)
				if (branchId !== undefined && branchId !== null && Number(branchId) !== 0) {
					params.set('BranchId', String(branchId))
				}
				// Add multiple Status parameters
				if (status && Array.isArray(status) && status.length > 0) {
					status.forEach(s => params.append('Status', s))
				} else if (status) {
					params.append('Status', String(status))
				}

				const urlWithParams = `/getTimesheetsTotals?${params.toString()}`
				return urlWithParams
			},
			providesTags: (res, err, filters) => [{ type: 'TimesheetTotals', id: JSON.stringify(filters) }],
			keepUnusedDataFor: REFRESH_TIMEOUT,
		}),
		timesheetReject: builder.mutation<Approval, Reject>({
			query: body => ({
				url: `/reject`,
				method: 'POST',
				body,
			}),
			async onQueryStarted(arg, api) {
				await invalidateTimesheetDetailOnSuccess(arg, api)
			},
		}),
		timesheetApproval: builder.mutation<Approval, ApprovalRequest>({
			query: body => ({
				url: `/approval`,
				method: 'POST',
				body,
			}),
			async onQueryStarted(arg, api) {
				await invalidateTimesheetDetailOnSuccess(arg, api)
			},
		}),
		timesheetEdit: builder.mutation<Approval, ApprovalRequest>({
			query: body => ({
				url: `/approval`,
				method: 'PUT',
				body,
			}),
			async onQueryStarted(arg, api) {
				await invalidateTimesheetDetailOnSuccess(arg, api)
			},
		}),
		timesheetEditTimesheet: builder.mutation<Approval, ApprovalRequest>({
			query: body => ({
				url: `/editTimesheet`,
				method: 'PUT',
				body,
			}),
			async onQueryStarted(arg, api) {
				await invalidateTimesheetDetailOnSuccess(arg, api)
			},
		}),
		timesheetPrepareForEdit: builder.mutation<Approval, number>({
			query: timesheetId => ({
				url: `/PrepareForEdit`,
				method: 'POST',
				body: JSON.stringify(timesheetId),
				headers: {
				'Content-Type': 'application/json',
				},
			}),
			async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
				try {
					await queryFulfilled
					dispatch(timesheetDetailApi.util.invalidateTags([{ type: 'TimesheetDetail' }]))
				} catch (err) {
					handleError(err, 'timesheetApi.ts:timesheetEditApproval')
				}
			},
		}),
	}),
})

// Export hooks for usage in functional components
export const {
	useGetTimesheetsQuery,
	useGetTimesheetsFilteredQuery,
	useGetTimesheetsTotalsQuery,
	useGetAllTimesheetsQuery,
	useGetTimesheetsForApprovalQuery,
	useTimesheetRejectMutation,
	useTimesheetApprovalMutation,
	useTimesheetEditMutation,
	useTimesheetEditTimesheetMutation,
	useTimesheetPrepareForEditMutation,
} = timesheetApi

// Helpers
function extractTimesheetId(arg: unknown): unknown {
	// @ts-expect-error - permissivo para aceitar diferentes shapes de body
	return arg && (arg.timesheetId ?? arg.timeSheetId ?? arg.id)
}

async function invalidateTimesheetDetailOnSuccess(
	arg: unknown,
	{ dispatch, queryFulfilled }: { dispatch: (a: unknown) => void; queryFulfilled: Promise<unknown> },
) {
	try {
		await queryFulfilled
		const id = extractTimesheetId(arg)
		if (id !== undefined && id !== null) {
			dispatch(timesheetDetailApi.util.invalidateTags([{ type: 'TimesheetDetail' }]))
		}
	} catch (err) {
		handleError(err, 'timesheetApi.ts:invalidateTimesheetDetailOnSuccess')
	}
}
